import { runtimeConfig } from '@/lib/runtime-config';

// Base URL
export function resolveApiBaseUrl({
  baseUrl,
  port,
  isDev,
}: {
  baseUrl?: string;
  port?: string;
  isDev: boolean;
}) {
  const resolvedBaseUrl = baseUrl?.trim() || (isDev ? 'http://localhost' : '');
  const resolvedPort = port?.trim() || (isDev ? '9000' : '');

  return resolvedPort ? `${resolvedBaseUrl}:${resolvedPort}` : resolvedBaseUrl;
}

export const PORT =
  runtimeConfig.VITE_API_PORT?.trim() || (import.meta.env.DEV ? '9000' : '');
export const BASE = resolveApiBaseUrl({
  baseUrl: runtimeConfig.VITE_API_BASE_URL,
  port: runtimeConfig.VITE_API_PORT,
  isDev: import.meta.env.DEV,
});

// API segments
export const API = '/api';
export const V1 = '/v1';

// Auth endpoints
export const URL_BACKOFFICE = '/backoffice';
export const URL_AUTH = '/auth';
export const URL_LOGIN = '/login';
export const URL_FORGET_PASSWORD = '/forget-password';
export const URL_RESET_PASSWORD = '/reset-password';
export const URL_LOGOUT = '/logout';
export const URL_LOGOUT_ALL = '/logout-all';

// Backoffice admin endpoints
export const URL_REFERRERS = '/referrers';
export const URL_ADVISORS = '/advisors';
export const URL_ADMINS = '/admins';
export const URL_FOREIGN_VENDORS = '/foreign-vendors';
export const URL_COMMISSIONS = '/commissions';
export const URL_TENANTS = '/tenants';
export const URL_ENABLE = '/enable';
export const URL_DOCUMENTS = '/documents';
export const URL_IMPORT = '/import';
export const URL_CASSETTO_FISCALE = '/cassetto-fiscale';
export const URL_EXPIRE = '/expire';
export const URL_DOWNLOAD_INVOICES = '/download-invoices';
export const URL_DISABLE = '/disable';
export const URL_SUMMARY = '/summary';
export const URL_APPROVE = '/approve';
export const URL_PAY = '/pay';
export const URL_CANCEL = '/cancel';
export const URL_BATCH = '/batch';
export const URL_ACTIVATE = '/activate';
export const URL_DEACTIVATE = '/deactivate';
export const URL_INVITE = '/invite';
export const URL_INVITATIONS = '/invitations';
export const URL_ACCEPT = '/accept';
export const URL_RESEND = '/resend';
export const URL_REOPEN = '/reopen';
export const URL_CHILDREN = '/children';
export const URL_RENEW_PLAN = '/renew-plan';
export const URL_PLANS = '/plans';
export const URL_PAYMENT = '/payment';
export const URL_RENEW = '/renew';
export const URL_SUBSCRIPTION = '/subscription';
export const URL_SUBSCRIPTIONS = '/subscriptions';
export const URL_REACTIVATE = '/reactivate';
export const URL_AUTO_RENEW = '/auto-renew';
export const URL_PREPAYMENT = '/prepayment';
export const URL_WAIVE_BLOCK = '/waive-block';
export const URL_UNPAID = '/unpaid';

// Acube Billing Cassetto Fiscale endpoints
export const URL_ACUBE = '/acube';
export const URL_BILLING = '/billing';

// Fisconline endpoints
export const URL_FISCONLINE = '/fisconline';
export const URL_SCHEDULE = '/schedule';
/** Scarichi automatici uno per tipo: `/schedules/:jobType` (+ `/activate`). */
export const URL_SCHEDULES = '/schedules';
export const URL_DOWNLOAD_ARCHIVE = '/download-archive';
export const URL_UNLINK = '/unlink';
export const URL_UPDATE_APPOINTEE_CREDENTIALS = '/update-appointee-credentials';
export const URL_DELEGATED_TENANTS = '/delegated-tenants';
export const URL_ASSIGN_APPOINTEE = '/assign-appointee';
export const URL_UNLINK_APPOINTEE = '/unlink-appointee';
export const URL_ACTIVATE_ACUBE_DELEGATION = '/activate-acube-delegation';
export const URL_DEACTIVATE_ACUBE_DELEGATION = '/deactivate-acube-delegation';
export const URL_HISTORY_DOWNLOAD = '/history-download';
export const URL_DOWNLOAD_JOBS = '/download-jobs';
export const URL_F24_SYNC = '/f24-sync';
export const URL_F24_SUMMARY = '/f24-summary';
export const URL_LATEST = '/latest';
export const URL_STATUS = '/status';

// Subscription Notifications endpoints
export const URL_SUBSCRIPTION_NOTIFICATIONS = '/subscription-notifications';

// Support Tickets endpoints (coda ticket del backoffice)
// `URL_STATS`, `URL_STATUS` e `URL_DOWNLOAD` sono gia' definiti sopra e si
// riusano: sono gli stessi segmenti di path, non costanti di dominio.
export const URL_SUPPORT_TICKETS = '/support-tickets';
export const URL_MESSAGES = '/messages';
export const URL_ASSIGN = '/assign';
export const URL_TRIAGE = '/triage';
export const URL_EVENTS = '/events';
export const URL_ATTACHMENTS = '/attachments';
export const URL_OPERATORS = '/operators';

// Admin Notifications endpoints
export const URL_ADMIN_NOTIFICATIONS = '/admin-notifications';
export const URL_PRODUCT_RELEASES = '/product-releases';

// Contracts endpoints
export const URL_CONTRACTS = '/contracts';
export const URL_SEND = '/send';
export const URL_SHARE = '/share';
export const URL_DOWNLOAD = '/download';
export const URL_PREVIEW_LINK = '/preview-link';
export const URL_UPLOAD = '/upload';

// Locations endpoints (geo: comuni/province, auth-only, non backoffice)
export const URL_LOCATIONS = '/locations';
export const URL_MUNICIPALITIES = '/municipalities';

// System Config endpoints
export const URL_SYSTEM_CONFIG = '/system-config';

// AI System Usage endpoints
export const URL_AI = '/ai';
export const URL_SYSTEM_USAGE = '/system-usage';

// Service Usage endpoints
export const URL_SERVICE_USAGE = '/service-usage';

// Referrer panel endpoints
export const URL_REFERRER = '/referrer';
export const URL_ME = '/me';
export const URL_DASHBOARD = '/dashboard';

// ACube Jobs endpoints
export const URL_ACUBE_JOBS = '/acube-jobs';

// Pagamenti SEPA: specchio ACube e bonifici non ACube (Fabrick/sconosciuti)
export const URL_ACUBE_PAYMENTS = '/acube-payments';
export const URL_SEPA_PAYMENTS = '/sepa-payments';
export const URL_SYNC = '/sync';
export const URL_RESUME_AUTO = '/resume-auto';
export const URL_ORPHAN_REVIEW = '/orphan-review';
/** Reset del bonifico collegato a una riga dello specchio ACube. */
export const URL_SEPA_PAYMENT = '/sepa-payment';

// E-invoice XML monitoring / backfill endpoints
export const URL_EINVOICE_XML = '/einvoice-xml';
export const URL_MISSING = '/missing';
export const URL_BACKFILL = '/backfill';

// Error Logs endpoints
export const URL_ERROR_LOGS = '/error-logs';
export const URL_CATEGORIES = '/categories';
export const URL_RESOLVE = '/resolve';
/** Destinatari email degli alert sugli errori: `/error-logs/alert-settings`. */
export const URL_ERROR_LOG_ALERT_SETTINGS = '/alert-settings';

// MCP Clients endpoints
export const URL_MCP_CLIENTS = '/mcp-clients';
export const URL_AUDIT_LOGS = '/audit-logs';

// Fabrick Migration endpoints
export const URL_FABRICK_MIGRATION = '/fabrick-migration';

// ACube Rollback endpoints (rientro da Fabrick ad ACube)
export const URL_ACUBE_ROLLBACK = '/acube-rollback';

// Sync Flow endpoints
export const URL_SYNC_FLOW = '/sync-flow';
export const URL_DOWNLOAD_MOVEMENTS = '/download/movements';

// System Monitor endpoints
export const URL_SYSTEM_MONITOR = '/system-monitor';
export const URL_REDIS = '/redis';
export const URL_QUEUES = '/queues';
export const URL_STATS = '/stats';
export const URL_USAGE = '/usage';
export const URL_VAT_LOOKUP = '/vat-lookup';
export const URL_STATISTICS = '/statistics';
export const URL_JOBS = '/jobs';
export const URL_SCHEDULED = '/scheduled';
export const URL_FAILED = '/failed';
export const URL_RECENT = '/recent';
export const URL_RETRY = '/retry';
export const URL_RETRY_ALL = '/retry-all';

// Ricerca globale della palette ⌘K
export const URL_SEARCH = '/search';
